/**
 * #1006 — whether Polymarket US is serving a market now, for the Tracker's
 * Close on a US holding (Brendan on PR #1116: Close was offered on a holding
 * whose market isn't being served, and its ticket then failed to read the
 * market).
 *
 * The ticket's market read (relay `polymarket-us-markets.ts`) refuses a
 * market the producer's catalogue doesn't hold, or holds without a current
 * book (no book, or one past its expiry), and the locked close ticket then
 * shows only "Market data unavailable". So 'served' is exactly "listed with a
 * current book", the state the ticket can read; anything else the catalogue
 * answers is 'not-served'. That can change by the second (a book lapses or
 * returns), so the answer is only as fresh as the last ask. A read that fails
 * is never 'not-served' — null, and Close stays offered; so is a market never
 * answered yet. While a new ask is out, the last answer stands.
 *
 * Read with the account-free catalogue (`listPublicMarkets({ slugs })`, 20 a
 * page: in-memory on the relay, no venue request), whenever the host asks —
 * the Tracker asks when the US read on screen changes, so this adds no timer.
 * Each ask re-reads; the latest ask wins, one read at a time.
 */
import type { UsMarketClient } from './marketClient';
import type { UsDiscoveryMarket } from './marketClient';

export type UsMarketServed = 'served' | 'not-served' | null;

const PAGE = 20;

export class UsMarketServing {
  private readonly answers = new Map<string, 'served' | 'not-served'>();
  private readonly books = new Map<string, UsDiscoveryMarket>();
  private readonly listeners = new Set<() => void>();
  private wanted: readonly string[] | null = null;
  private activeSlugs = new Set<string>();
  private running = false;
  private closed = false;
  private generation = 0;
  private version = 0;

  constructor(private readonly client: Pick<UsMarketClient, 'listPublicMarkets' | 'invalidate'>) {}

  get(slug: string): UsMarketServed {
    return this.answers.get(slug) ?? null;
  }
  /** The same bounded catalogue read used by Close; no extra request or timer. */
  getMarket(slug: string): UsDiscoveryMarket | null { return this.books.get(slug) ?? null; }

  getVersion = (): number => this.version;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Re-read these markets; an ask while a read runs replaces what is left of it. */
  request(slugs: Iterable<string>): void {
    this.wanted = [...new Set(slugs)];
    this.activeSlugs = new Set(this.wanted);
    let removed = false;
    // Hold only the current portfolio's markets, not every holding ever visited.
    for (const slug of this.answers.keys()) if (!this.activeSlugs.has(slug)) {
      this.answers.delete(slug); this.books.delete(slug); removed = true;
    }
    if (removed) {
      this.version++;
      for (const listener of this.listeners) listener();
    }
    void this.pump();
  }

  /** Mounted (again): StrictMode's replayed unmount must not leave it shut,
   * and an ask made while it was shut is read now. */
  open(): void {
    this.closed = false;
    if (this.wanted) void this.pump();
  }

  /** Shut (unmounted): drop the ask; a late answer is ignored. */
  close(): void {
    this.generation++;
    this.closed = true;
    this.wanted = null;
    this.client.invalidate();
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.closed && this.wanted) {
        const slugs = this.wanted;
        const generation = this.generation;
        this.wanted = null;
        for (let i = 0; i < slugs.length && !this.closed && !this.wanted; i += PAGE) {
          const page = slugs.slice(i, i + PAGE);
          let next: Map<string, 'served' | 'not-served'> | null = null;
          let received: readonly UsDiscoveryMarket[] = [];
          try {
            const { markets } = await this.client.listPublicMarkets({ slugs: page });
            received = markets;
            next = new Map(page.map((slug) => {
              const market = markets.find((m) => m.marketSlug === slug);
              return [slug, market?.book.state === 'current' ? 'served' : 'not-served'];
            }));
          } catch {
            // Catalogue unavailable or the read refused: unknown, never "not served".
          }
          // StrictMode can reopen the same instance before this await settles.
          // Neither a success nor an abort from its retired mount can publish.
          // Leave the new mount's queued ask intact for the next loop.
          if (this.closed || generation !== this.generation) break;
          let changed = false;
          for (const slug of page) {
            // A newer portfolio can remove a market while this page is in flight.
            if (!this.activeSlugs.has(slug)) continue;
            const book = received.find(m => m.marketSlug === slug);
            const previous = this.books.get(slug);
            const evidence = (m: UsDiscoveryMarket | undefined) => m ? JSON.stringify([m.marketId, m.book, m.classification, m.timeEvidence]) : null;
            if (evidence(previous) !== evidence(book)) changed = true;
            if (book) this.books.set(slug, book);
            else this.books.delete(slug);
            const answer = next?.get(slug);
            if (this.answers.get(slug) === answer) continue;
            if (answer) this.answers.set(slug, answer);
            else this.answers.delete(slug);
            changed = true;
          }
          if (changed) {
            this.version++;
            for (const listener of this.listeners) listener();
          }
        }
      }
    } finally {
      this.running = false;
    }
  }
}
